/* eslint-disable react-hooks/exhaustive-deps */
import { Popper, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useMemo } from "react";
import { FastField, getIn } from "formik";
import ErrorIcon from "./ErrorIcon";
import configDefaultForm from "./configDefault";
import RequiredLabel from "./RequiredLabel";
import { forwardRef } from "react";

const AutoWidthPopper = (props) => {
  const {
    style: { width },
  } = props;
  const style = { minWidth: "fit-content", width };
  return <Popper {...props} style={style} />;
};

const GlobitsSelectInput = (props, ref) => {
  return (
    <FastField
      {...props}
      name={props.name}
      shouldUpdate={shouldComponentUpdate}
    >
      {({ field, meta, form }) => (
        <Component
          {...props}
          field={field}
          meta={meta}
          setFieldValue={form.setFieldValue}
          ref={ref}
        />
      )}
    </FastField>
  );
};

const Component = forwardRef((props, ref) => {
  const {
    disabled,
    fullWidth = configDefaultForm.fullWidth,
    label,
    name,
    size = configDefaultForm.size,
    variant = configDefaultForm.variant,
    className = "",
    required,
    requiredLabel,
    onChange,
    readOnly,
    value,
    displayLabel = "name",
    displayValue = "value",
    options = [],
    field,
    meta,
    setFieldValue,
    getOptionLabel,
    defaultValue = null,
    disableClearable,
    getOptionDisabled,
    onKeyDown,
    noNullOption = false,
    disableTyping,
    getOptionSelected,
    getValueInputProps,
    placeholder,
    autoWidthPopper = false,
    freeSolo = false,
    tabIndex,
    autoUpercase,
    multiple = false,
    ...otherProps
  } = props;

  const isError = meta && meta.touched && meta.error;
  const disLabel = label ? (
    <>
      {label} <RequiredLabel requiredLabel={requiredLabel || required} />
    </>
  ) : (
    ""
  );

  const getOptionLabelDefault = useMemo(
    () =>
      getOptionLabel
        ? getOptionLabel
        : (option) => {
          let label = "";
          if (option === field?.value) {
            const item = options?.find(
              (e) => getIn(e, displayValue) === option
            );
            label = item
              ? getIn(item, displayLabel)
              : typeof option === "string"
                ? option
                : "";
          } else {
            label = getIn(option, displayLabel) ?? "";
          }
          if (autoUpercase) {
            return label.toUpperCase();
          }
          return label;
        },
    [getOptionLabel]
  );

  const handleChange = (event, value) => {
    let newValue;

    if (multiple) {
      // khi multiple thì value là mảng object
      newValue = value || [];
    } else {
      if (value && typeof value === "object") {
        newValue = getIn(value, displayValue) ?? null;
      } else {
        newValue = value ?? null;
      }
    }
    setFieldValue(name, newValue);
    if (onChange) {
      const e = {
        ...event,
        target: {
          ...event.target,
          value: newValue,
          id: name,
          name: name,
        },
      };
      onChange(e);
    }
  };

  const onInputChange = (event, newInputValue, reason) => {
    if (freeSolo && reason === "input") {
      setFieldValue(name, newInputValue);
      if (onChange) {
        const e = {
          ...event,
          target: {
            ...event?.target,
            name: name,
            value: newInputValue,
          },
        };
        onChange(e);
      }
    }
  };

  return (
    <>
      {!fullWidth && disLabel && <label htmlFor={name}>{disLabel}</label>}
      <Autocomplete
        {...field}
        {...otherProps}
        ref={ref}
        id={name}
        name={name}
        multiple={multiple}
        disabled={disabled || readOnly}
        fullWidth={fullWidth}
        size={size}
        value={
          multiple
            ? field?.value || [] // array khi multiple
            : options?.find((opt) => getIn(opt, displayValue) === field?.value) ??
            (freeSolo ? field?.value ?? "" : null)
        }
        onChange={handleChange}
        options={options || []}
        getOptionLabel={getOptionLabelDefault}
        getOptionSelected={
          getOptionSelected
            ? getOptionSelected
            : (option, value) => {
              if (multiple) {
                return option?.[displayValue] === value?.[displayValue];
              }
              return getIn(option, displayValue) === value;
            }
        }
        onInputChange={onInputChange}
        noOptionsText="Không có dữ liệu"
        autoHighlight
        openOnFocus
        disableClearable={readOnly || disableClearable || noNullOption}
        readOnly={readOnly}
        PopperComponent={autoWidthPopper ? AutoWidthPopper : undefined}
        getOptionDisabled={getOptionDisabled}
        freeSolo={freeSolo}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.stopPropagation();
            event.preventDefault();
          }

          if (onKeyDown) {
            onKeyDown(event);
          }
          return true;
        }}
        renderInput={(params) => {
          if (!params?.inputProps?.value && field?.value !== undefined) {
            const item = options?.find(
              (e) => getIn(e, displayValue) === field.value
            );
            if (freeSolo) {
              params.inputProps.value = field.value || "";
            } else {
              params.inputProps.value = item ? getIn(item, displayLabel) : "";
            }
          }

          return (
            <TextField
              {...params}
              label={fullWidth && disLabel ? disLabel : null}
              variant={variant}
              error={isError}
              helperText={fullWidth && isError ? meta.error : ""}
              className={`input-container ${className} ${readOnly ? "read-only-autocomplete" : ""
                }`}
              InputLabelProps={{
                htmlFor: name,
              }}
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
                readOnly: readOnly || disableTyping,
                // value: getValueInputProps ? getValueInputProps(params?.inputProps?.value) :
                //   ((params?.inputProps?.value !== null || params?.inputProps?.value !== undefined) ?
                //     params?.inputProps?.value : null),
                value: getValueInputProps
                  ? getValueInputProps(params?.inputProps?.value)
                  : params?.inputProps?.value !== null ||
                    params?.inputProps?.value !== undefined
                    ? autoUpercase
                      ? params?.inputProps?.value?.toUpperCase()
                      : params?.inputProps?.value // Viết hoa giá trị trong input
                    : null,
                tabIndex: tabIndex,
              }}
            />
          );
        }}
      />
      {isError && !fullWidth && <ErrorIcon helperText={meta.error} />}
    </>
  );
});

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps?.options !== currentProps?.options ||
  nextProps.name !== currentProps.name ||
  nextProps.value !== currentProps.value ||
  nextProps.onChange !== currentProps.onChange ||
  nextProps.label !== currentProps.label ||
  nextProps.required !== currentProps.required ||
  nextProps.disabled !== currentProps.disabled ||
  nextProps.readOnly !== currentProps.readOnly ||
  nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
  Object.keys(nextProps).length !== Object.keys(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
  getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
  getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
  getIn(currentProps.formik.touched, currentProps.name);

export default React.memo(forwardRef(GlobitsSelectInput));
